/**
 * The whole app, replaced, once the session's access has ended.
 *
 * `PERM005` (see `./accessEnded`) means the organization membership behind this
 * session was removed or deactivated, or its organization was closed — a
 * destroyed demo included. Every request from here on is refused the same way,
 * so there is nothing left for a page to show: rendering the routed app behind a
 * notice would only paint one "couldn't load" per panel underneath it. The gate
 * renders the notice INSTEAD of the app, which also unmounts every provider and
 * stream that would otherwise keep asking.
 *
 * The way out depends on the session:
 *
 *  - an ordinary session signs out;
 *  - an admin "Connect as" session ends the impersonation instead
 *    (`endImpersonationSession`). Plain sign-out there would sweep the shared
 *    Auth0 cache and sign the operator out of their own tab next door;
 *  - a demo account (`demo_expires_at` on the token) is told the demo ended and
 *    where to ask about a workspace of its own.
 *
 * No organization switcher, although the header has one. Its feed
 * (`/my-organizations`) and its switch (`/active-organization`) are API calls
 * like any other, and the authorizer refuses them with the same `PERM005`
 * because it gates every request on the TOKEN's organization membership — the
 * one that ended. A control here could only fail.
 */
import * as React from "react";
import { FlaskConical, ShieldAlert, UserX } from "lucide-react";
import { isImpersonating, type AutheClaims } from "@authe/fe-commons/auth";
import { Button } from "@authe/ui-core";
import { useSession } from "@authe/ui-core/session";
import { EXTERNAL_LINKS } from "@/config/constants";
import { DASHBOARD_ERRORS } from "@/config/errors";
import { readDemoExpiry } from "./DemoBanner";
import { composeTitle } from "./documentTitle";
import { logoutDestination } from "./logout-destination";
import { endImpersonationSession } from "./session";
import { useEndedAccess, type EndedAccess } from "./accessEnded";

export interface AccessEndedGateProps {
  children: React.ReactNode;
  /**
   * Called once, after the app has been taken down. The host drops its
   * in-memory caches here: nothing may render the departed organization's data
   * again, and by now nothing is mounted to refetch it.
   */
  onEnded?: () => void;
}

export function AccessEndedGate({ children, onEnded }: AccessEndedGateProps): React.ReactElement {
  const ended = useEndedAccess();
  const onEndedRef = React.useRef(onEnded);
  React.useEffect(() => {
    onEndedRef.current = onEnded;
  });
  React.useEffect(() => {
    if (ended) onEndedRef.current?.();
  }, [ended]);
  if (!ended) return <>{children}</>;
  return <AccessEndedPage ended={ended} />;
}

export function AccessEndedPage({ ended }: { ended: EndedAccess }): React.ReactElement {
  const { session, logout } = useSession();
  const demo = readDemoExpiry(session?.accessToken) !== null;
  const impersonating = isImpersonating(session?.claims as AutheClaims | undefined);
  const copy = demo ? DASHBOARD_ERRORS.DEMO_ENDED : DASHBOARD_ERRORS.ACCESS_ENDED;
  const [leaving, setLeaving] = React.useState(false);

  // The router's title provider is gone with the app, so the tab would keep
  // naming whatever page was open when access ended.
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = composeTitle(demo ? "Demo ended" : "Access ended", undefined);
  }, [demo]);

  const leave = () => {
    setLeaving(true);
    if (impersonating) {
      endImpersonationSession();
      return;
    }
    // The same destination as the profile menu's Sign out: the login SPA's
    // signed-out view, which does not sign straight back in.
    void logout({ returnTo: logoutDestination() }).catch(() => setLeaving(false));
  };

  const Icon = demo ? FlaskConical : impersonating ? ShieldAlert : UserX;

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
      <div role="alert" className="flex max-w-md flex-col items-center text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <Icon className="size-6" aria-hidden />
        </div>
        <h1 className="text-lg font-semibold">{copy.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{copy.description}</p>
        {/* The server's own words say WHICH ending it was (membership gone,
            membership deactivated, organization closed). A demo needs no more
            than "it ended". */}
        {!demo && ended.reason ? (
          <p className="mt-1 text-xs text-muted-foreground">{ended.reason.replace(/\.?$/, ".")}</p>
        ) : null}
        {impersonating ? (
          <p className="mt-3 text-sm text-muted-foreground">
            You are connected to this account as another user. End the session to go back to your
            own.
          </p>
        ) : null}
        <div className="mt-6 flex flex-col items-center gap-3">
          <Button onClick={leave} disabled={leaving}>
            {impersonating ? "End impersonation session" : "Sign out"}
          </Button>
          {demo ? (
            <a
              href={`${EXTERNAL_LINKS.discuss}?topic=pricing`}
              target="_blank"
              rel="noreferrer"
              className="text-sm underline underline-offset-2 hover:no-underline"
            >
              Talk to us about your own workspace
            </a>
          ) : null}
        </div>
      </div>
    </main>
  );
}
